import React from 'react';

import { lazySlotFallback, lazySlotRenderer } from './lazySlotBoundary';
import type { FlatSku } from '@/api/credits';

export const STAND_SIMULATION_WORKFLOW_SLOT_KEY = 'stand.simulation' as const;
export const STAND_CORRECTION_WORKFLOW_SLOT_KEY = 'stand.correction' as const;
export const PROPERTY_PACKAGE_WORKFLOW_SLOT_KEY = 'property.package' as const;
export const BUYER_DECODE_WORKFLOW_SLOT_KEY = 'property.buyer-decode' as const;

export type GovernedReportWorkflowSlotKey =
  | typeof STAND_SIMULATION_WORKFLOW_SLOT_KEY
  | typeof STAND_CORRECTION_WORKFLOW_SLOT_KEY
  | typeof PROPERTY_PACKAGE_WORKFLOW_SLOT_KEY
  | typeof BUYER_DECODE_WORKFLOW_SLOT_KEY;

export interface BuyerDecodeCheckoutContext {
  propertyId: string;
  valuationRef: string;
  inventoryRevision: string;
  priceSnapshotRef: string;
  priceRegion: string;
  priceWindowMonths: 12 | 24 | 36;
  returnPath: string;
}

export type FmCatalogPurchaseWorkflowState<Context> =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | {
      kind: 'ready';
      sku: FlatSku;
      context: Context;
      submitting: boolean;
    };

export interface FmCatalogPurchaseWorkflowProps<Context> {
  testId: string;
  open: boolean;
  onCancel: () => void;
  onRetry: () => void;
  onCheckout: (context: Context) => void;
  state: FmCatalogPurchaseWorkflowState<Context>;
  title: string;
  subtitle: string;
  emptyLabel: string;
  checkoutCopy: {
    description: string;
    propertyCount: string;
    buy: string;
    starting: string;
    cancel: string;
    retry: string;
  };
}

export type BuyerDecodeWorkflowState = FmCatalogPurchaseWorkflowState<BuyerDecodeCheckoutContext>;
export type BuyerDecodeWorkflowProps = FmCatalogPurchaseWorkflowProps<BuyerDecodeCheckoutContext>;

export interface FmReportWorkflowSlotDescriptor<Props extends object = object> {
  kind: 'slot';
  id: string;
  slotKey: GovernedReportWorkflowSlotKey;
  slotProps: Props;
}

type ReportWorkflowSlotRenderer = (props: object) => React.ReactElement;
type ReportWorkflowSlotModule<Props extends object> = {
  default: React.ComponentType<Props>;
};

const REPORT_WORKFLOW_SLOT_REGISTRY = new Map<
  GovernedReportWorkflowSlotKey,
  ReportWorkflowSlotRenderer
>();

/**
 * Register a code-split workflow renderer from the application composition
 * root. Report routes pass semantic, controlled props; the archetype resolves
 * the visual form/modal without importing page-domain components.
 */
export function registerLazyReportWorkflowSlot<Props extends { open: boolean }>(
  key: GovernedReportWorkflowSlotKey,
  loader: () => Promise<ReportWorkflowSlotModule<Props>>,
  fallback?: React.ReactNode,
): void {
  // The registration owns the suspension it creates, for the same reason as
  // registerLazyMapSlot: without a boundary here the
  // pending chunk suspends to App.tsx's route-level fallback, which hides the
  // WHOLE application with display:none - blurring whatever the owner had
  // focused and closing any open menu or list under them.
  //
  // Its fallback must not be `null`: this boundary is nearer than anything
  // the workflow's own frame owns, so a null fallback would preempt them and an
  // owner who opened a workflow would get an empty modal with nothing in it while the chunk loaded.
  // `lazySlotRenderer` serves an observable loading affordance -- the SAME
  // helper the map registry uses, so the two identical sites cannot drift apart
  // -- and takes an override.
  //
  // But a report workflow is NOT a map. `FmReportWorkflows` renders every
  // declared workflow unconditionally and the `open` flag is honoured INSIDE
  // the lazily-loaded component, so a CLOSED workflow is in the tree on every
  // report page and its chunk loads there. A fixed affordance would paint
  // a stray spinner in the page body for a workflow nobody had opened -- and,
  // because `waitForVisibleAfterSpinners` treats any ring as "something is
  // genuinely pending" (tests/utils/plannerReadiness.ts), it would also make every
  // report route's readiness gate wait on a chunk the owner never asked for.
  //
  // So the affordance is served only while the workflow is OPEN. A closed one
  // has no frame for it to fill and renders nothing.
  const whileOpen = (props: Props): React.ReactNode =>
    props.open === true ? lazySlotFallback(key) : null;
  REPORT_WORKFLOW_SLOT_REGISTRY.set(
    key,
    lazySlotRenderer(
      key,
      loader,
      fallback === undefined ? whileOpen : fallback,
    ) as ReportWorkflowSlotRenderer,
  );
}

export function getReportWorkflowSlot(
  key: GovernedReportWorkflowSlotKey,
): ReportWorkflowSlotRenderer {
  const renderer = REPORT_WORKFLOW_SLOT_REGISTRY.get(key);
  if (!renderer) {
    throw new Error(`No governed report workflow renderer is registered for "${key}".`);
  }
  return renderer;
}

export function reportWorkflowSlot<Props extends object>(
  descriptor: FmReportWorkflowSlotDescriptor<Props>,
): FmReportWorkflowSlotDescriptor<Props> {
  return descriptor;
}
